import { ApiError, parseApiError } from 'AuthBff/api-error';
import type { TenantSummary } from 'AuthBff/types';
import { boundedFetch } from 'AuthSdk/bounded-fetch';

/** What `createAuthFetch` returns: a `fetch` that carries the session's bearer
 *  token and refreshes it once on a 401 before giving up. */
export type AuthFetch = (input: string, init?: RequestInit) => Promise<Response>;

export type AuthFetchDeps = {
  getAccessToken: () => string | null;
  setAccessToken: (token: string) => void;
  onUnauthenticated: () => void;
  /** Optional: holds the OIDC ID token, forwarded as `X-Id-Token` for server-side step-up freshness. */
  getIdToken?: () => string | null;
  setIdToken?: (token: string) => void;
  onStepUpRequired?: () => void;
};

export type RefreshSessionResult = {
  accessToken: string | null;
  idToken?: string;
  activeTenant?: TenantSummary | null;
};

// A company that requires two-factor refuses with a step-up marked `twoFactor`: a fresh
// sign-in cannot satisfy it, so the console shows its two-factor page instead.
const demandsTwoFactor = (details: unknown): boolean => typeof details === 'object' && details !== null && 'twoFactor' in details && details.twoFactor === true;

const REFRESH_LOCK = 'bomdisoft_auth_refresh';

const DEFAULT_TIMEOUTS = { lockMs: 15_000, meMs: 10_000, refreshMs: 10_000 };

export type AuthTimeouts = typeof DEFAULT_TIMEOUTS;

let timeouts: AuthTimeouts = { ...DEFAULT_TIMEOUTS };

export const authTimeouts = (): AuthTimeouts => timeouts;

let refreshPromise: Promise<RefreshSessionResult> | null = null;

const fetchRefresh = async (): Promise<RefreshSessionResult> => {
  const res = await boundedFetch('/api/auth/refresh', { method: 'POST' }, timeouts.refreshMs);
  // 401 means the refresh token was rejected: a definite "no session". Other
  // failures (5xx, auth infra down) throw, because the session state is unknown.
  if (res.status === 401) return { accessToken: null };
  if (!res.ok) throw new ApiError(res.status, { code: 'REFRESH_UNAVAILABLE', message: 'refresh failed' });
  // A signed-out visitor gets 200 with `authenticated: false` rather than a
  // 401, so an ok response is not on its own proof of a token.
  const body = (await res.json()) as { accessToken?: string; idToken?: string; activeTenant?: TenantSummary | null };
  if (!body.accessToken) return { accessToken: null };
  return { accessToken: body.accessToken, activeTenant: body.activeTenant, idToken: body.idToken };
};

export const refreshSession = (): Promise<RefreshSessionResult> => {
  if (refreshPromise) return refreshPromise;
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  const pending: Promise<RefreshSessionResult> = locks ? locks.request(REFRESH_LOCK, { signal: AbortSignal.timeout(timeouts.lockMs) }, fetchRefresh) : fetchRefresh();
  refreshPromise = pending.finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
};

const refreshOnce = async (deps: AuthFetchDeps): Promise<string> => {
  const session = await refreshSession();
  if (!session.accessToken) throw new ApiError(401, { code: 'UNAUTHENTICATED', message: 'refresh failed' });
  deps.setAccessToken(session.accessToken);
  if (session.idToken) deps.setIdToken?.(session.idToken);
  return session.accessToken;
};

export const createAuthFetch =
  (deps: AuthFetchDeps) =>
  async (input: string, init?: RequestInit): Promise<Response> => {
    const buildHeaders = (token: string | null) => {
      const h = new Headers(init?.headers);
      if (token) h.set('authorization', `Bearer ${token}`);
      const idToken = deps.getIdToken?.();
      if (idToken) h.set('x-id-token', idToken);
      if (!h.has('x-request-id')) h.set('x-request-id', crypto.randomUUID());
      return h;
    };
    const refusal = async (res: Response): Promise<ApiError> => {
      const error = await parseApiError(res);
      if (error.status === 403 && error.code === 'STEP_UP_REQUIRED' && !demandsTwoFactor(error.details)) deps.onStepUpRequired?.();
      return error;
    };
    let res = await fetch(input, { ...init, headers: buildHeaders(deps.getAccessToken()) });
    if (res.status !== 401) {
      if (res.ok) return res;
      throw await refusal(res);
    }
    let newToken: string;
    try {
      newToken = await refreshOnce(deps);
    } catch {
      deps.onUnauthenticated();
      throw new ApiError(401, { code: 'UNAUTHENTICATED', message: 'session expired' });
    }
    res = await fetch(input, { ...init, headers: buildHeaders(newToken) });
    if (res.ok) return res;
    throw await refusal(res);
  };

export const __resetForTests = () => {
  refreshPromise = null;
  timeouts = { ...DEFAULT_TIMEOUTS };
};

export const __setTimeoutsForTests = (next: Partial<AuthTimeouts> | null) => {
  timeouts = { ...DEFAULT_TIMEOUTS, ...(next ?? {}) };
};
